import React, { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl-next';

import DropdownMenu, {
	type CustomTriggerProps,
	DropdownItem,
	DropdownItemGroup,
	type OnOpenChangeArgs,
} from '@atlaskit/dropdown-menu';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import GrowVerticalIcon from '@atlaskit/icon/core/grow-vertical';
import { ButtonItem } from '@atlaskit/menu';
import {
	ButtonMenuItem,
	COLLAPSE_ELEM_BEFORE,
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
	MenuList,
} from '@atlaskit/navigation-system';
import { token } from '@atlaskit/tokens';

import type { NavItemReorderingProps } from '../../../types';

import messages from './messages';

export function NavItemReorderingMenu({
	TriggerComponent = ButtonItem,
	canMoveUp,
	canMoveDown,
	moveUp,
	moveDown,
	moveToTop,
	moveToBottom,
	onOpenChange,
}: NavItemReorderingProps) {
	const { formatMessage } = useIntl();
	const isOnly = !canMoveUp && !canMoveDown;
	const [open, setOpen] = useState(false);

	const onOpenChangeInternal = useCallback(
		(args: OnOpenChangeArgs) => {
			setOpen(args.isOpen);
			onOpenChange?.(args);
		},
		[onOpenChange],
	);

	const iconProps = useMemo(() => {
		if (TriggerComponent === ButtonItem) {
			return {
				iconBefore: <GrowVerticalIcon label="" />,
				iconAfter: <ChevronRightIcon color={token('color.icon.subtle')} label="" size="small" />,
			};
		}

		return {
			elemBefore: <GrowVerticalIcon label="" />,
			elemAfter: <ChevronRightIcon color={token('color.icon.subtle')} label="" size="small" />,
		};
	}, [TriggerComponent]);

	if (TriggerComponent === FlyoutMenuItemTrigger) {
		if (isOnly) {
			// FlyoutMenuItemTrigger doesn't support isDisabled prop,
			// so if the trigger should be disabled we need to render something visually compatible that does support that state
			return (
				<ButtonMenuItem {...iconProps} isDisabled={true}>
					{formatMessage(messages.reorderingMenuTriggerLabel)}
				</ButtonMenuItem>
			);
		}

		// TODO: MenuList -> ButtonMenuItem is the closest visually to DropdownItemGroup -> DropdownItem, but they have different role attributes which is not ideal.
		return (
			<FlyoutMenuItem isOpen={open}>
				<FlyoutMenuItemTrigger
					{...iconProps}
					onClick={(event) => onOpenChangeInternal({ isOpen: !open, event })}
				>
					{formatMessage(messages.reorderingMenuTriggerLabel)}
				</FlyoutMenuItemTrigger>
				<FlyoutMenuItemContent>
					<MenuList>
						<ButtonMenuItem
							isDisabled={!canMoveUp}
							onClick={(event) => {
								moveToTop();
								onOpenChangeInternal({ isOpen: false, event });
							}}
							elemBefore={COLLAPSE_ELEM_BEFORE}
						>
							{formatMessage(messages.moveToTop)}
						</ButtonMenuItem>
						<ButtonMenuItem
							isDisabled={!canMoveUp}
							onClick={(event) => {
								moveUp();
								onOpenChangeInternal({ isOpen: false, event });
							}}
							elemBefore={COLLAPSE_ELEM_BEFORE}
						>
							{formatMessage(messages.moveUp)}
						</ButtonMenuItem>
						<ButtonMenuItem
							isDisabled={!canMoveDown}
							onClick={(event) => {
								moveDown();
								onOpenChangeInternal({ isOpen: false, event });
							}}
							elemBefore={COLLAPSE_ELEM_BEFORE}
						>
							{formatMessage(messages.moveDown)}
						</ButtonMenuItem>
						<ButtonMenuItem
							isDisabled={!canMoveDown}
							onClick={(event) => {
								moveToBottom();
								onOpenChangeInternal({ isOpen: false, event });
							}}
							elemBefore={COLLAPSE_ELEM_BEFORE}
						>
							{formatMessage(messages.moveToBottom)}
						</ButtonMenuItem>
					</MenuList>
				</FlyoutMenuItemContent>
			</FlyoutMenuItem>
		);
	}

	return (
		<DropdownMenu
			shouldRenderToParent
			spacing="compact"
			placement="right-start"
			trigger={({ triggerRef, ...triggerProps }: CustomTriggerProps) => {
				return (
					<TriggerComponent {...iconProps} {...triggerProps} ref={triggerRef} isDisabled={isOnly}>
						{formatMessage(messages.reorderingMenuTriggerLabel)}
					</TriggerComponent>
				);
			}}
			onOpenChange={onOpenChangeInternal}
			isOpen={isOnly ? false : open}
		>
			<DropdownItemGroup>
				<DropdownItem isDisabled={!canMoveUp} onClick={moveToTop}>
					{formatMessage(messages.moveToTop)}
				</DropdownItem>
				<DropdownItem isDisabled={!canMoveUp} onClick={moveUp}>
					{formatMessage(messages.moveUp)}
				</DropdownItem>
				<DropdownItem isDisabled={!canMoveDown} onClick={moveDown}>
					{formatMessage(messages.moveDown)}
				</DropdownItem>
				<DropdownItem isDisabled={!canMoveDown} onClick={moveToBottom}>
					{formatMessage(messages.moveToBottom)}
				</DropdownItem>
			</DropdownItemGroup>
		</DropdownMenu>
	);
}
